import {
  Activity,
  Beaker,
  Droplets,
  LineChart,
  Sparkles,
  Sprout,
} from "lucide-react";

import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";

// LandingPage is the public, logged-out home. The server serves the SPA shell
// for an unauthenticated GET of "/" (every other route still bounces to auth),
// and the root route renders this when the session-presence breadcrumb is
// absent. The Log In / Sign Up buttons are plain same-origin links to the
// backend's /auth/login and /auth/signup handlers, which 302 to the auth
// service deep links — so AUTH_BASE_URL and the client_id never enter the
// bundle.

const FEATURES = [
  {
    icon: Sprout,
    title: "Track every grow",
    body: "A live card per active grow — current chemistry, deltas from ideal, and the next action you actually need to take.",
  },
  {
    icon: Droplets,
    title: "Guided refills",
    body: "An adaptive refill wizard walks reservoir top-offs and full changes step by step, with calibration built in.",
  },
  {
    icon: Beaker,
    title: "Chemistry profiles",
    body: "Water sources, nutrients, and recipes in one place. Build a profile once and reuse it across grows.",
  },
  {
    icon: Activity,
    title: "Live pen monitoring",
    body: "pH, EC, and temperature stream from your BLE pen to a glanceable status strip you can read across the room.",
  },
  {
    icon: Sparkles,
    title: "AI assistance",
    body: "Diagnose a struggling grow or look up plant targets with AI — every call logged and auditable.",
  },
  {
    icon: LineChart,
    title: "Full history",
    body: "Refill timelines, audit logs, and pending actions keep a tidy record of what changed and why.",
  },
] as const;

export function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Top bar */}
      <header className="border-b">
        <div className="container mx-auto flex h-14 items-center justify-between px-4 md:px-6">
          <span className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <Sprout className="h-5 w-5 text-primary" aria-hidden />
            joesgrove
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <a href="/auth/login">Log In</a>
            </Button>
            <Button size="sm" asChild>
              <a href="/auth/signup">Sign Up</a>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1">
        <section className="container mx-auto px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
              <Sprout className="h-3.5 w-3.5 text-primary" aria-hidden />
              Hydroponic grow management
            </span>
            <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">
              Keep every reservoir
              <span className="block text-primary">dialed in.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
              joesgrove tracks your grows, guides every refill, and watches your
              pH &amp; EC in real time — so the next right move is always one
              glance away.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button size="lg" asChild>
                <a href="/auth/signup">Get started — it&apos;s free</a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="/auth/login">Log in</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="border-t bg-muted/20">
          <div className="container mx-auto px-4 py-16 md:px-6 md:py-20">
            <div className="grid gap-px overflow-hidden rounded-xl border bg-border sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <div key={title} className="bg-background p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="mt-4 text-base font-semibold tracking-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="container mx-auto px-4 py-16 text-center md:px-6 md:py-24">
          <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
            Ready to grow with less guesswork?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-muted-foreground">
            Sign in with your account to pick up where your reservoirs left off.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button size="lg" asChild>
              <a href="/auth/signup">Create an account</a>
            </Button>
            <Button size="lg" variant="ghost" asChild>
              <a href="/auth/login">I already have one</a>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t">
        <div className="container mx-auto px-4 py-6 text-center text-xs text-muted-foreground md:px-6">
          © joesgrove
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
